/**
 * Puzzle 2 — Light Path. Needs both cats:
 *
 *   Basil seats three colored glass lenses. He picks a lens up by walking
 *   over it (it rides above his head) and lifts it into its pedestal's
 *   receiver column: an ordinary jump reaches the red and green pedestals,
 *   the blue one 8 u up takes Overclocked Leap. With all three seated the
 *   beam lights and the Star Crystal appears.
 *
 *   Minna wakes the lit crystal with Calming Resonance: she climbs the
 *   purr perch under it (plat_s2_8 in ClockworkCanopyPlatforms) and holds
 *   F within the purr radius until it charges (~4 s). Nothing else charges
 *   it. The crystal used to charge itself once the last lens was seated,
 *   which let Basil finish the puzzle alone
 *   (tools/playtest/scenarios/solo-solve.mjs).
 *
 * Aura Sight is not required: the pedestals' colored receiver guides are
 * always visible. It still outlines the pedestals and shows the dormant
 * crystal before the beam reaches it.
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PointLight } from "@babylonjs/core/Lights/pointLight";

import { CrystalReceiver } from "../../../entities/interactives/CrystalReceiver";
import { GameEvents } from "../../../config/events";
import { CLOCKWORK_CANOPY } from "../../../config/GameConfig";
import { tagAs } from "../../PlaceholderTag";

import type { InteractiveInfo } from "../../../abilities/minna/AuraSight";
import type { CueDef } from "../../../ui/tutorialCues";
import type { PuzzleContext, PuzzleCue, PuzzleModule, PuzzleState } from "../../PuzzleModule";
import type { PuzzleQAHints } from "../../qa/PuzzleQAHints";

interface LensDef {
  key: string;
  color: string;
  pos: Vector3;
  pedestalPos: Vector3;
  /** How Basil gets the carried lens into the receiver column. */
  lift: "jump" | "leap";
}

const LENS_DEFS: LensDef[] = [
  {
    key: "lensRed",
    color: CLOCKWORK_CANOPY.COLORS.LENS_RED,
    pos: new Vector3(30, 2.5, 0),
    pedestalPos: new Vector3(35, 6.3, 0),
    lift: "jump",
  },
  {
    key: "lensGreen",
    color: CLOCKWORK_CANOPY.COLORS.LENS_GREEN,
    pos: new Vector3(38, 2.5, 0),
    pedestalPos: new Vector3(40, 8.3, 0),
    lift: "jump",
  },
  {
    key: "lensBlue",
    color: CLOCKWORK_CANOPY.COLORS.LENS_BLUE,
    pos: new Vector3(46, 2.5, 0),
    pedestalPos: new Vector3(45, 10.3, 0),
    lift: "leap",
  },
];

/** The Star Crystal on plat_s2_6. Minna's purr perch (plat_s2_8) is under it. */
export const LIGHT_PATH_CRYSTAL_POSITION = { x: 50, y: 10.3, z: 0 } as const;
/** Layout name of the ledge Minna purrs the crystal from. */
export const LIGHT_PATH_PURR_PERCH = "plat_s2_8";

/** Purring out of range this close to the crystal gets a "come closer" toast. */
const PURR_HINT_RADIUS = 12;
/** Seconds between "come closer" toasts. */
const PURR_HINT_INTERVAL = 4;

const OBJECTIVE_LENSES = "Light the lens path";
const OBJECTIVE_CRYSTAL = "Wake the Star Crystal";

const CUE_GRAB_LENS: CueDef = { cat: "basil", parts: [{ action: "move" }, { text: "grab the lens" }] };
const CUE_JUMP_TO_PEDESTAL: CueDef = { cat: "basil", parts: [{ action: "jump" }, { text: "onto the pedestal" }] };
const CUE_LEAP_TO_PEDESTAL: CueDef = { cat: "basil", parts: [{ action: "leap" }, { text: "onto the pedestal" }] };
const CUE_PURR_CRYSTAL: CueDef = { cat: "minna", parts: [{ action: "purr", hold: true }, { text: "wake the crystal" }] };

/** Cue chips sit this far above the thing to act on. */
const CUE_ANCHOR_LIFT = 1.2;
const above = (p: { x: number; y: number; z: number }) =>
  Object.freeze({ x: p.x, y: p.y + CUE_ANCHOR_LIFT, z: p.z });

/** One frozen cue per step, so getCue() returns the same object while the step holds. */
const GRAB_LENS_CUES: readonly PuzzleCue[] = LENS_DEFS.map((ld) =>
  Object.freeze({ objective: OBJECTIVE_LENSES, cue: CUE_GRAB_LENS, anchor: above(ld.pos) }),
);
const CARRY_LENS_CUES: readonly PuzzleCue[] = LENS_DEFS.map((ld) =>
  Object.freeze({
    objective: OBJECTIVE_LENSES,
    cue: ld.lift === "leap" ? CUE_LEAP_TO_PEDESTAL : CUE_JUMP_TO_PEDESTAL,
    anchor: above(ld.pedestalPos),
  }),
);
const WAKE_CRYSTAL_CUE: PuzzleCue = Object.freeze({
  objective: OBJECTIVE_CRYSTAL,
  cue: CUE_PURR_CRYSTAL,
  anchor: above(LIGHT_PATH_CRYSTAL_POSITION),
});

/**
 * Basil does not have to land on a single pedestal pixel while carrying
 * a heavy lens. Pedestals behave like magnetic brass receivers: if the
 * carried lens enters the visible receiver column, it snaps into place.
 */
export const LIGHT_PATH_PEDESTAL_PLACEMENT = {
  horizontalRadius: 2.0,
  minYOffset: -2.2,
  maxYOffset: 2.2,
} as const;

const RECEIVER_PANEL_VISIBILITY = 0.72;
const RECEIVER_PANEL_ALPHA = 0.28;
const RECEIVER_PANEL_DEPTH = 0.04;
// Guide panels/rails sit BEHIND the play plane (cats at z=0, camera at
// z=+9, so positive z is toward the camera). These were +1.02/+1.08,
// which drew the translucent acceptance volumes in FRONT of the cats —
// the worst QA-visual leak in the game (2026-07-10 review, finding #5).
// Negative z keeps them readable against the pedestals without ever
// occluding a player.
const RECEIVER_PANEL_Z = -1.02;
const RECEIVER_RAIL_ALPHA = 0.78;
const RECEIVER_RAIL_WIDTH = 0.08;
const RECEIVER_RAIL_DEPTH = 0.06;
const RECEIVER_RAIL_Z = -1.08;

export function isInsideLightPathPedestalZone(
  lensPosition: Vector3,
  pedestalPosition: Vector3,
): boolean {
  const dx = lensPosition.x - pedestalPosition.x;
  const dz = lensPosition.z - pedestalPosition.z;
  const horizontalDistance = Math.sqrt(dx * dx + dz * dz);
  const dy = lensPosition.y - pedestalPosition.y;
  return (
    horizontalDistance <= LIGHT_PATH_PEDESTAL_PLACEMENT.horizontalRadius &&
    dy >= LIGHT_PATH_PEDESTAL_PLACEMENT.minYOffset &&
    dy <= LIGHT_PATH_PEDESTAL_PLACEMENT.maxYOffset
  );
}

export class LightPathPuzzle implements PuzzleModule {
  readonly id = "lightPath";
  readonly displayName = "Light Path";

  private ctx!: PuzzleContext;

  private lensMeshes: Mesh[] = [];
  private lensMats: StandardMaterial[] = [];
  private pedestalMeshes: Mesh[] = [];
  private pedestalMats: PBRMaterial[] = [];
  private receiverGuideMeshes: Mesh[] = [];
  private receiverGuideMats: StandardMaterial[] = [];
  private receiverGuideMatsByLens: Array<{ panel: StandardMaterial; rail: StandardMaterial }> = [];
  private lensPlaced: boolean[] = [false, false, false];

  /**
   * Index of the lens Basil is currently carrying (-1 if none). The
   * original implementation used a "Basil within 2 of lens AND within
   * 2.5 of pedestal at the same time" check — impossible because each
   * lens/pedestal pair is ~4 units apart, leaving the puzzle unsolvable.
   * The L3 QA pass flagged this as the only hard blocker in the level.
   * Carry-then-place fixes it without breaking the existing pedestal
   * snap and beam-activation logic downstream.
   */
  private carriedLensIndex = -1;

  private lightCrystal!: CrystalReceiver;
  /** The crystal's Aura Sight entry: hidden until the beam lights it. */
  private crystalInfo!: InteractiveInfo;
  private beamSource!: PointLight;

  /** All three lenses seated: the crystal is lit and takes Minna's Purr. */
  private beamLit = false;
  private lightPathComplete = false;

  /** Sim time of the last "come closer" toast (rate limit). */
  private lastTooFarHintAt = -Infinity;
  private darkCrystalHintShown = false;

  setup(ctx: PuzzleContext): void {
    this.ctx = ctx;
    const { scene, shadowGenerator } = ctx;

    for (const ld of LENS_DEFS) {
      // Lens on ground (pickup)
      const lens = MeshBuilder.CreateCylinder(
        `${ld.key}_lens`,
        { height: 0.1, diameter: 1.2, tessellation: 32 },
        scene,
      );
      // Clone: carrying moves the lens mesh's position in place, which
      // used to move LENS_DEFS itself, so a restarted level spawned each
      // carried lens where it had last been carried.
      lens.position = ld.pos.clone();
      const lensMat = new StandardMaterial(`${ld.key}_mat`, scene);
      lensMat.diffuseColor = Color3.FromHexString(ld.color);
      lensMat.emissiveColor = Color3.FromHexString(ld.color).scale(0.3);
      lensMat.alpha = 0.7;
      lens.material = lensMat;
      this.lensMeshes.push(lens);
      this.lensMats.push(lensMat);

      // Pedestal (target — high up, needs OverclockedLeap)
      const pedestal = MeshBuilder.CreateCylinder(
        `${ld.key}_pedestal`,
        { height: 0.6, diameter: 1.4, tessellation: 12 },
        scene,
      );
      pedestal.position = ld.pedestalPos.clone();
      const pedMat = new PBRMaterial(`${ld.key}_pedMat`, scene);
      pedMat.albedoColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.COPPER_DARK);
      pedMat.metallic = 0.6;
      pedMat.roughness = 0.4;
      pedMat.emissiveColor = new Color3(0.05, 0.03, 0.01);
      pedestal.material = pedMat;
      this.pedestalMeshes.push(pedestal);
      this.pedestalMats.push(pedMat);
      shadowGenerator.addShadowCaster(pedestal);
      this.createPedestalReceiverGuide(scene, ld);

      ctx.registerInteractive({
        mesh: lens,
        hidden: false,
        state: "revealed",
      });
      // Pedestals are plain brass receivers, visible to both cats (their
      // colored receiver guides mark each one). Aura Sight outlines them
      // but is not needed to find them. They used to register as hidden,
      // which made them vanish whenever Aura Sight switched off.
      ctx.registerInteractive({
        mesh: pedestal,
        hidden: false,
        state: "revealed",
      });
    }

    // ── Star crystal: dormant and hidden (Aura Sight shows it) until the
    //    beam lights it; then it stays visible and takes Minna's Purr. ──
    this.lightCrystal = new CrystalReceiver(scene, {
      key: "lightCrystal",
      position: new Vector3(
        LIGHT_PATH_CRYSTAL_POSITION.x,
        LIGHT_PATH_CRYSTAL_POSITION.y,
        LIGHT_PATH_CRYSTAL_POSITION.z,
      ),
      hidden: true,
      label: "Star Crystal",
    });
    this.crystalInfo = {
      mesh: this.lightCrystal.rootMesh,
      hidden: true,
      state: "hidden",
      onReveal: () => this.lightCrystal.reveal(),
      onConceal: () => this.lightCrystal.conceal(),
    };
    ctx.registerInteractive(this.crystalInfo);

    // ── Beam source (hidden until lenses placed) ──
    this.beamSource = new PointLight(
      "beamSource",
      new Vector3(28, 12, -1),
      scene,
    );
    this.beamSource.diffuse = new Color3(1.0, 0.95, 0.8);
    this.beamSource.intensity = 0;
    this.beamSource.range = 30;

    // ── Crystal activation fires the win condition ──
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CRYSTAL_ACTIVATED, (key) => {
        if (key === "lightCrystal" && !this.lightPathComplete) {
          this.lightPathComplete = true;
          ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, {
            puzzleId: this.id,
          });
          ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
            message: "Star Crystal charged! Light path complete.",
            color: CLOCKWORK_CANOPY.COLORS.LENS_GOLD,
          });
        }
      }),
    );

  }

  update(dt: number, elapsed: number): void {
    const { basil, minna, calmingResonance } = this.ctx;
    const basilPos = basil.mesh.position;

    // ── Carry: if Basil is holding a lens, make it ride above his head
    //    and check for placement on the matching pedestal each frame.
    if (this.carriedLensIndex >= 0) {
      const idx = this.carriedLensIndex;
      if (this.lensPlaced[idx]) {
        // Defensive — should be unreachable but clears the carry flag if
        // some other path placed the lens out from under us.
        this.carriedLensIndex = -1;
      } else {
        const lensMesh = this.lensMeshes[idx];
        lensMesh.position.x = basilPos.x;
        lensMesh.position.y = basilPos.y + 1.0;
        lensMesh.position.z = basilPos.z;

        const pedestalMesh = this.pedestalMeshes[idx];
        if (isInsideLightPathPedestalZone(lensMesh.position, pedestalMesh.position)) {
          this.lensPlaced[idx] = true;
          this.carriedLensIndex = -1;
          lensMesh.position = pedestalMesh.position.clone();
          lensMesh.position.y += 0.4;
          this.markReceiverGuidePlaced(idx);

          const placedCount = this.lensPlaced.filter(Boolean).length;
          // Forensic-QA event: concrete placement moment. PUZZLE_PROGRESSED
          // fires alongside for back-compat with the puzzle-step tracker.
          this.ctx.gameEvents.emit(GameEvents.LENS_PLACED, {
            lensKey: LENS_DEFS[idx].key,
            pedestalKey: `${LENS_DEFS[idx].key}_pedestal`,
            placedCount,
            required: this.lensPlaced.length,
          });
          this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
            puzzleId: this.id,
            step: `lens${idx}Placed`,
          });
          if (this.lensPlaced.every(Boolean)) {
            this.lightTheBeam();
          } else {
            this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
              message: "Lens placed on pedestal!",
              color: LENS_DEFS[idx].color,
            });
          }
        }
      }
    }

    // ── Pickup: if Basil isn't carrying anything, check proximity to
    //    each unplaced lens. First lens within range gets picked up.
    if (this.carriedLensIndex < 0) {
      for (let i = 0; i < this.lensMeshes.length; i++) {
        if (this.lensPlaced[i]) continue;
        const lensMesh = this.lensMeshes[i];
        if (Vector3.Distance(basilPos, lensMesh.position) < 1.6) {
          this.carriedLensIndex = i;
          // Forensic-QA event: lets telemetry pair the pickup with its
          // eventual placement and measure how long each lens takes.
          this.ctx.gameEvents.emit(GameEvents.LENS_PICKED_UP, {
            lensKey: LENS_DEFS[i].key,
            carrier: "basil",
          });
          this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
            message: "Lens picked up — climb to its glowing pedestal.",
            color: LENS_DEFS[i].color,
          });
          break;
        }
      }
    }

    // ── Minna's part: only her Purr, within its radius, charges the lit
    //    crystal (the win condition is CRYSTAL_ACTIVATED('lightCrystal')).
    //    Before the beam is lit it takes no charge at all — an ungated
    //    charge once let Minna solve the light path without a lens.
    if (calmingResonance.isActive && !this.lightPathComplete) {
      const crystalDist = Vector3.Distance(
        minna.mesh.position,
        this.lightCrystal.rootMesh.position,
      );
      const inRange = crystalDist <= calmingResonance.getRadius();
      if (this.beamLit) {
        this.lightCrystal.updateCharge(dt, inRange);
        if (
          !inRange &&
          crystalDist <= PURR_HINT_RADIUS &&
          elapsed - this.lastTooFarHintAt > PURR_HINT_INTERVAL
        ) {
          this.lastTooFarHintAt = elapsed;
          this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
            message: "Too far for the purr. Minna, climb the brass perch under the Star Crystal.",
          });
        }
      } else if (inRange && !this.darkCrystalHintShown) {
        this.darkCrystalHintShown = true;
        this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "The Star Crystal is dark. Basil has to seat all three lenses first.",
        });
      }
    }
  }

  /**
   * HUD cue for the current step (see PuzzleCue): Basil fetches the
   * next lens, lifts it onto its pedestal, and once all three are seated
   * Minna purrs the crystal awake. Null once solved.
   */
  getCue(): PuzzleCue | null {
    if (this.lightPathComplete) return null;
    if (this.beamLit) return WAKE_CRYSTAL_CUE;
    if (this.carriedLensIndex >= 0) return CARRY_LENS_CUES[this.carriedLensIndex] ?? null;
    return GRAB_LENS_CUES[this.lensPlaced.indexOf(false)] ?? null;
  }

  /**
   * All three lenses are seated: the beam lights the Star Crystal. It
   * stays visible from now on (out of Aura Sight's hide/show set) and
   * waits for Minna's Purr — there is no auto-charge.
   */
  private lightTheBeam(): void {
    if (this.beamLit) return;
    this.beamLit = true;
    this.beamSource.intensity = 3.0;
    this.lightCrystal.reveal();
    this.crystalInfo.hidden = false;
    this.crystalInfo.state = "revealed";
    this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
      message: "The beam finds the Star Crystal! Minna, purr it awake from the perch below.",
      color: CLOCKWORK_CANOPY.COLORS.LENS_GOLD,
    });
  }

  getState(): PuzzleState {
    const placed = this.lensPlaced.filter(Boolean).length;
    return {
      solved: this.lightPathComplete,
      // Placement is 90% of the progress — the final 10% is the crystal
      // charging phase after all three lenses are in place.
      progress: this.lightPathComplete
        ? 1
        : Math.min(0.9, (placed / this.lensPlaced.length) * 0.9),
    };
  }

  /**
   * Sub-puzzle state seeder — `__qa.startAtPuzzle` passes this through
   * as `options.seedState`. Shape:
   *   { lensesPlaced?: number[] }  // indices 0..2 to mark pre-placed.
   *
   * Replicates the side-effects of the placement branch in `update()`:
   * snaps the lens mesh to its pedestal, flips the `lensPlaced[i]` flag,
   * and — if all three are placed — lights the beam. Seeding never
   * charges the crystal: Minna's Purr is still needed to solve.
   */
  seed(state: Record<string, unknown>): void {
    const placedIdxs = Array.isArray(state?.lensesPlaced)
      ? (state.lensesPlaced as unknown[]).filter(
          (n): n is number => typeof n === "number" && n >= 0 && n < this.lensMeshes.length,
        )
      : [];
    for (const idx of placedIdxs) {
      if (this.lensPlaced[idx]) continue;
      this.lensPlaced[idx] = true;
      const lensMesh = this.lensMeshes[idx];
      const pedestalMesh = this.pedestalMeshes[idx];
      if (lensMesh && pedestalMesh) {
        lensMesh.position = pedestalMesh.position.clone();
        lensMesh.position.y += 0.4;
      }
      this.markReceiverGuidePlaced(idx);
      const placedCount = this.lensPlaced.filter(Boolean).length;
      this.ctx.gameEvents.emit(GameEvents.LENS_PLACED, {
        lensKey: LENS_DEFS[idx].key,
        pedestalKey: `${LENS_DEFS[idx].key}_pedestal`,
        placedCount,
        required: this.lensPlaced.length,
      });
      this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
        puzzleId: this.id,
        step: `lens${idx}Placed`,
      });
    }
    if (this.lensPlaced.every(Boolean)) this.lightTheBeam();
  }

  getQAHints(): PuzzleQAHints {
    return {
      puzzleId: this.id,
      successEvent: GameEvents.PUZZLE_SOLVED,
      targets: [
        { id: "lens_0",     position: { x: 30, y: 2.5 },  radius: 1.2, allowedCat: "either", label: "Lens 0 pickup" },
        { id: "pedestal_0", position: { x: 35, y: 6.3 },  radius: LIGHT_PATH_PEDESTAL_PLACEMENT.horizontalRadius, allowedCat: "either", label: "Lens 0 pedestal receiver" },
        { id: "lens_1",     position: { x: 38, y: 2.5 },  radius: 1.2, allowedCat: "either", label: "Lens 1 pickup" },
        { id: "pedestal_1", position: { x: 40, y: 8.3 },  radius: LIGHT_PATH_PEDESTAL_PLACEMENT.horizontalRadius, allowedCat: "either", label: "Lens 1 pedestal receiver" },
        { id: "lens_2",     position: { x: 46, y: 2.5 },  radius: 1.2, allowedCat: "either", label: "Lens 2 pickup" },
        { id: "pedestal_2", position: { x: 45, y: 10.3 }, radius: LIGHT_PATH_PEDESTAL_PLACEMENT.horizontalRadius, allowedCat: "either", label: "Lens 2 pedestal receiver" },
        { id: "beacon",     position: { x: 50, y: 10.3 }, radius: 1.5, allowedCat: "minna",  label: "Star Crystal (lit by the lenses, woken by Minna's Purr)" },
        // Top of plat_s2_8: every point on it is inside the purr radius.
        { id: "purr_perch", position: { x: 50, y: 4.2 },  radius: 1.5, allowedCat: "minna",  label: "Minna's purr perch under the Star Crystal" },
      ],
      traversals: [
        // Each lens carry is an OvLeap with air-drift. Basil grabs the
        // lens, fires `4` while holding a directional key, then releases
        // to let AIR_DECEL brake over the pedestal.
        {
          id: "basil_ovleap_east_to_pedestal_0",
          kind: "ovLeap",
          cat: "basil",
          from: "lens_0",
          to: "pedestal_0",
          successWindowMs: 4500,
          recipe: [
            { key: "d", holdMs: 100 },
            { key: "4", holdMs: 100, delayMs: 0 },
            { key: "d", holdMs: 900, delayMs: 0 },
          ],
        },
        {
          id: "basil_ovleap_east_to_pedestal_1",
          kind: "ovLeap",
          cat: "basil",
          from: "lens_1",
          to: "pedestal_1",
          successWindowMs: 4500,
          recipe: [
            { key: "d", holdMs: 100 },
            { key: "4", holdMs: 100, delayMs: 0 },
            { key: "d", holdMs: 700, delayMs: 0 },
          ],
        },
        // pedestal_2 sits WEST of lens_2 — drift is `d` (west).
        //
        // CRITICAL: plat_s2_5 (x=[43, 47], pedestal_2's platform) is
        // directly above plat_s2_7 (x=[44, 48], where lens_2 sits).
        // Firing OvLeap from x<47 while drifting west hits plat_s2_5's
        // bottom during ascent — basil bounces off the ceiling and
        // misses pedestal_2 entirely. The player MUST walk east past
        // plat_s2_5's east edge (x=47) before the leap, then drift
        // west during flight so the arc clears plat_s2_5 and lands
        // on top. Verified via MCP walkthrough 2026-04-19.
        {
          id: "basil_ovleap_west_to_pedestal_2",
          kind: "ovLeap",
          cat: "basil",
          from: "lens_2",
          to: "pedestal_2",
          successWindowMs: 4500,
          recipe: [
            // Walk EAST (`a`) past plat_s2_5's east edge. ~300ms at
            // MOVE_SPEED=5 covers ~1.5u east from lens_2 at x=46.
            { key: "d", holdMs: 300 },
            { key: "4", holdMs: 100, delayMs: 0 },
            { key: "a", holdMs: 700, delayMs: 0 },
          ],
        },
        // Running-jump from plat_s2_2 east edge to plat_s2_7. After
        // placing lens_1 at pedestal_1, basil falls onto plat_s2_2
        // (top y=2.2). The 4u gap to plat_s2_7 (with too-narrow
        // plat_s2_7_step at x=[41.75, 43.25]) makes the navigator
        // oscillate; an OvLeap straight up hits plat_s2_4's bottom
        // (basil is INSIDE plat_s2_4's x range x=[38, 42] when on
        // plat_s2_2). A normal running jump (peak y≈5.6) clears
        // plat_s2_4's bottom (y=7.8) AND covers the 4u gap east at
        // MOVE_SPEED=5 over the 1.2s airtime.
        // Minna's hop from plat_s2_7 (top 2.2) up onto the purr perch
        // plat_s2_8 (top 4.2, 0.5 u further right). Jump BEFORE the east
        // edge: a cat that runs off it passes under the perch and drops
        // to the floor. Holding right through the arc carries her onto
        // the perch.
        {
          id: "minna_jump_plat_s2_7_to_purr_perch",
          kind: "jump",
          cat: "minna",
          from: "lens_2",
          to: "purr_perch",
          successWindowMs: 3_000,
          recipe: [
            { keyDown: "d" },
            { waitMs: 120 },
            { keyDown: " " },
            { waitMs: 260 },
            { keyUp: " " },
            { waitMs: 350 },
            { keyUp: "d" },
          ],
        },
        {
          id: "running_jump_plat_s2_2_to_plat_s2_7",
          kind: "jump",
          cat: "basil",
          from: "lens_1",
          to: "lens_2",
          successWindowMs: 4_000,
          recipe: [
            { key: "d", holdMs: 200 },
            { key: " ", holdMs: 80, delayMs: 0 },
            { key: "d", holdMs: 900, delayMs: 0 },
          ],
        },
      ],
    };
  }

  dispose(): void {
    for (let i = 0; i < this.lensMeshes.length; i++) {
      this.lensMeshes[i].dispose();
      this.lensMats[i].dispose();
      this.pedestalMeshes[i].dispose();
      this.pedestalMats[i].dispose();
    }
    this.lightCrystal.dispose();
    this.beamSource.dispose();
    for (const mesh of this.receiverGuideMeshes) {
      mesh.dispose();
    }
    for (const mat of this.receiverGuideMats) {
      mat.dispose();
    }
    this.receiverGuideMeshes = [];
    this.receiverGuideMats = [];
    this.receiverGuideMatsByLens = [];
  }

  private createPedestalReceiverGuide(scene: PuzzleContext["scene"], lens: LensDef): void {
    const color = Color3.FromHexString(lens.color);
    const radius = LIGHT_PATH_PEDESTAL_PLACEMENT.horizontalRadius;
    const height =
      LIGHT_PATH_PEDESTAL_PLACEMENT.maxYOffset -
      LIGHT_PATH_PEDESTAL_PLACEMENT.minYOffset;
    const yOffset =
      (LIGHT_PATH_PEDESTAL_PLACEMENT.maxYOffset +
        LIGHT_PATH_PEDESTAL_PLACEMENT.minYOffset) /
      2;
    const center = new Vector3(
      lens.pedestalPos.x,
      lens.pedestalPos.y + yOffset,
      RECEIVER_PANEL_Z,
    );

    const panelMat = new StandardMaterial(`${lens.key}_receiver_panel_mat`, scene);
    panelMat.diffuseColor = color;
    panelMat.emissiveColor = color.scale(0.55);
    panelMat.alpha = RECEIVER_PANEL_ALPHA;
    panelMat.specularColor = Color3.Black();
    panelMat.disableLighting = true;
    panelMat.metadata = { preserveAffordanceEmissive: true };
    this.receiverGuideMats.push(panelMat);

    const panel = MeshBuilder.CreateBox(
      `${lens.key}_receiver_panel`,
      {
        width: radius * 2,
        height,
        depth: RECEIVER_PANEL_DEPTH,
      },
      scene,
    );
    panel.position = center;
    panel.visibility = RECEIVER_PANEL_VISIBILITY;
    panel.isPickable = false;
    panel.material = panelMat;
    tagAs(panel, "modular-final");
    this.receiverGuideMeshes.push(panel);

    const railMat = new StandardMaterial(`${lens.key}_receiver_rail_mat`, scene);
    railMat.diffuseColor = color;
    railMat.emissiveColor = color.scale(0.85);
    railMat.alpha = RECEIVER_RAIL_ALPHA;
    railMat.specularColor = Color3.Black();
    railMat.disableLighting = true;
    railMat.metadata = { preserveAffordanceEmissive: true };
    this.receiverGuideMats.push(railMat);
    this.receiverGuideMatsByLens.push({ panel: panelMat, rail: railMat });

    const railCenter = new Vector3(center.x, center.y, RECEIVER_RAIL_Z);
    this.addReceiverGuideBox(
      scene,
      `${lens.key}_receiver_rail_left`,
      { width: RECEIVER_RAIL_WIDTH, height, depth: RECEIVER_RAIL_DEPTH },
      new Vector3(railCenter.x - radius, railCenter.y, railCenter.z),
      railMat,
    );
    this.addReceiverGuideBox(
      scene,
      `${lens.key}_receiver_rail_right`,
      { width: RECEIVER_RAIL_WIDTH, height, depth: RECEIVER_RAIL_DEPTH },
      new Vector3(railCenter.x + radius, railCenter.y, railCenter.z),
      railMat,
    );
    this.addReceiverGuideBox(
      scene,
      `${lens.key}_receiver_rung_bottom`,
      { width: radius * 2, height: RECEIVER_RAIL_WIDTH, depth: RECEIVER_RAIL_DEPTH },
      new Vector3(
        railCenter.x,
        railCenter.y + LIGHT_PATH_PEDESTAL_PLACEMENT.minYOffset,
        railCenter.z,
      ),
      railMat,
    );
    this.addReceiverGuideBox(
      scene,
      `${lens.key}_receiver_rung_mid`,
      { width: radius * 2, height: RECEIVER_RAIL_WIDTH, depth: RECEIVER_RAIL_DEPTH },
      railCenter,
      railMat,
    );
    this.addReceiverGuideBox(
      scene,
      `${lens.key}_receiver_rung_top`,
      { width: radius * 2, height: RECEIVER_RAIL_WIDTH, depth: RECEIVER_RAIL_DEPTH },
      new Vector3(
        railCenter.x,
        railCenter.y + LIGHT_PATH_PEDESTAL_PLACEMENT.maxYOffset,
        railCenter.z,
      ),
      railMat,
    );
  }

  private addReceiverGuideBox(
    scene: PuzzleContext["scene"],
    name: string,
    size: { width: number; height: number; depth: number },
    position: Vector3,
    material: StandardMaterial,
  ): void {
    const guide = MeshBuilder.CreateBox(name, size, scene);
    guide.position = position;
    guide.isPickable = false;
    guide.material = material;
    tagAs(guide, "modular-final");
    this.receiverGuideMeshes.push(guide);
  }

  private markReceiverGuidePlaced(index: number): void {
    const mats = this.receiverGuideMatsByLens[index];
    if (!mats) return;
    mats.panel.alpha = 0.1;
    mats.panel.emissiveColor = mats.panel.diffuseColor.scale(0.24);
    mats.rail.alpha = 0.35;
    mats.rail.emissiveColor = mats.rail.diffuseColor.scale(0.38);
  }
}
